import type { SignupPage } from "@ternary/contracts"
import { Avatar, Badge, Skeleton, ThemeRoot, ToastProvider } from "@ternary/ui"
import {
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react"
import { useTranslation } from "react-i18next"
import { useThemeColor } from "../theme-color.js"

/** The tenant's logo, or its initials when there is none or it fails to load. */
export function BrandLogo({ name, src }: { name: string; src: string | null }) {
  const [failed, setFailed] = useState<string | null>(null)
  if (src && src !== failed) {
    // The company name is written beside it, so the image itself is decorative.
    return <img alt="" className="signup-logo" onError={() => setFailed(src)} src={src} />
  }
  return <Avatar name={name} />
}

/**
 * The product's uploaded image in the brand panel (R70). The product's name is written beside it,
 * so it is decorative (m8). It shows from 768px only, at a fixed height, so it never makes the page
 * scroll; one that fails to load is left out.
 */
function ProductImage({ src }: { src: string | null }) {
  const [failed, setFailed] = useState<string | null>(null)
  if (!src || src === failed) return null
  return <img alt="" className="signup-brand-media" onError={() => setFailed(src)} src={src} />
}

// The background video plays only in the panel from 768px, never in a phone's brand bar (it would
// download the whole file for a strip), and never for a visitor who asked for reduced motion.
const BACKGROUND_VIDEO_QUERY = "(min-width: 48rem) and (prefers-reduced-motion: no-preference)"

function subscribeToVideoQuery(onChange: () => void) {
  if (typeof window.matchMedia !== "function") return () => {}
  const list = window.matchMedia(BACKGROUND_VIDEO_QUERY)
  list.addEventListener("change", onChange)
  return () => list.removeEventListener("change", onChange)
}

/** Whether the background video may play; false where there is no matchMedia (jsdom). */
function useVideoAllowed() {
  return useSyncExternalStore(
    subscribeToVideoQuery,
    () =>
      typeof window.matchMedia === "function" && window.matchMedia(BACKGROUND_VIDEO_QUERY).matches,
    () => false,
  )
}

/**
 * The tenant's background video behind the brand panel's text, in the picture's box, muted and
 * looping, with the picture (or nothing) as its poster. It is decoration: hidden from assistive
 * technology, never focused or controlled. One that fails to load is taken away, so the picture
 * or the brand gradient under it shows.
 */
function BackgroundVideo({ poster, src }: { poster: string | null; src: string }) {
  const [failed, setFailed] = useState<string | null>(null)
  if (src === failed) return null
  const fail = () => setFailed(src)
  return (
    // biome-ignore lint/a11y/noAriaHiddenOnFocusable: a video without controls is never focusable.
    <video
      aria-hidden="true"
      autoPlay
      className="signup-brand-video"
      loop
      muted
      onError={fail}
      playsInline
      poster={poster ?? undefined}
    >
      {/* A source that cannot load fires its own error, not the video's. */}
      <source onError={fail} src={src} />
    </video>
  )
}

/**
 * The signup frame in the tenant's brand (ThemeRoot): from 768px the brand panel beside the form,
 * on a phone a bar above it. The tenant's checkout background, when set, covers the panel or the
 * bar, and never stays fixed; its background video, when set, plays over it in the panel. Over
 * either, the text is white on a dark scrim behind the text alone. `banner` (the cookie banner)
 * goes last, in the page flow, so it reserves its own height. It also sets the tab title and the
 * browser's theme colour.
 */
export function SignupShell({
  page,
  children,
  banner,
}: {
  page: Pick<SignupPage, "branding" | "product">
  children: ReactNode
  banner?: ReactNode
}): ReactElement {
  const { t } = useTranslation()
  const { branding, product } = page

  useEffect(() => {
    document.title = `${product.name} · ${branding.companyName}`
  }, [branding.companyName, product.name])
  useThemeColor(branding.primaryColor, branding.theme === "dark" ? "dark" : "light")

  const videoAllowed = useVideoAllowed()
  const background = branding.checkoutBackgroundUrl
    ? ({
        "--signup-background": `url(${JSON.stringify(branding.checkoutBackgroundUrl)})`,
      } as CSSProperties)
    : undefined
  const video = videoAllowed ? branding.checkoutBackgroundVideoUrl : null
  // Styled for the picture while the video loads, and after it failed, so the text never moves.
  const media = Boolean(background || video)

  return (
    <ThemeRoot brand={branding.primaryColor} className="signup-shell" mode={branding.theme}>
      {/* Inside the tenant's theme, so a dark tenant gets dark toasts (m12). */}
      <ToastProvider dismissLabel={t("Dismiss")} label={t("Notifications")}>
        <main className="signup-main">
          <div
            className={media ? "signup-brand signup-brand-image" : "signup-brand"}
            style={background}
          >
            {video ? <BackgroundVideo poster={branding.checkoutBackgroundUrl} src={video} /> : null}
            <div className="signup-brand-identity">
              <BrandLogo name={branding.companyName} src={branding.logoUrl} />
              <span className="signup-brand-name">{branding.companyName}</span>
            </div>
            <div className="signup-brand-body">
              <ProductImage src={product.imageUrl} />
              <span>
                <Badge tone="primary">{t("Free")}</Badge>
              </span>
              <p className="signup-brand-product">{product.name}</p>
              {product.description ? (
                <p className="signup-brand-description">{product.description}</p>
              ) : null}
            </div>
            <p className="signup-brand-foot">{t("Signing up takes about a minute.")}</p>
          </div>
          <div className="signup-panel">
            <div className="signup-content">{children}</div>
          </div>
        </main>
        {banner}
      </ToastProvider>
    </ThemeRoot>
  )
}

/** The page before the signup page has loaded: the frame without a brand, and a status. */
export function SignupLoading({ label }: { label?: string }): ReactElement {
  const { t } = useTranslation()
  return (
    <ThemeRoot className="signup-shell">
      <main className="signup-main signup-state">
        <div
          aria-busy="true"
          aria-label={label ?? t("Loading signup")}
          className="signup-loading"
          role="status"
        >
          <Skeleton height="2rem" width="70%" />
          <Skeleton lines={4} />
        </div>
      </main>
    </ThemeRoot>
  )
}
